import { useEffect, useRef, useState } from "react";
import cn from "classnames";
import { Text } from "../Text/Text";
import Styles from "./PillDropdown.module.scss";

export interface PillDropdownProps {
  options: string[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
  "data-testid"?: string;
}

/**
 * A pill-shaped dropdown button (e.g. "Standard ⌄") that opens a small menu of
 * options. Modeled on the GameChanger app's stats sub-view selector. Closes on
 * outside click or Escape.
 */
export const PillDropdown = ({ options, value, onChange, className, ...rest }: PillDropdownProps) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const select = (option: string) => {
    onChange(option);
    setOpen(false);
  };

  return (
    <div ref={ref} className={cn(Styles.wrapper, className)}>
      <button
        type="button"
        className={cn(Styles.pill, { [Styles.open]: open })}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid={rest["data-testid"]}
      >
        <Text className={Styles.pillLabel} size="small" weight="semibold" color={open ? "gc-blue" : "cool-grey-dark"}>
          {value}
        </Text>
        <span className={cn(Styles.chevron, { [Styles.chevronOpen]: open })} aria-hidden="true">
          <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.75">
            <path d="M4 6l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </button>
      {open && (
        <ul className={Styles.menu} role="listbox">
          {options.map((option) => (
            <li key={option} role="option" aria-selected={option === value}>
              <button
                type="button"
                className={cn(Styles.menuItem, { [Styles.menuItemActive]: option === value })}
                onClick={() => select(option)}
              >
                {option}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default PillDropdown;
